import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authService } from './services/authService';
import { UserProvider, useUser } from './contexts/UserContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import TicketForm from './pages/TicketForm';
import ExergyIssueForm from './pages/ExergyIssueForm';
import TicketDetail from './pages/TicketDetail';
import TechnicianView from './pages/TechnicianView';
import SlaReport from './pages/SlaReport';
import SlaConfiguration from './pages/SlaConfiguration';
import UserManagement from './pages/UserManagement';
import Settings from './pages/Settings';
import SystemSettings from './pages/SystemSettings';
import EmailConfiguration from './pages/EmailConfiguration';
import Reports from './pages/Reports';
import TicketChurnReport from './pages/TicketChurnReport';
import TicketCategoryBreakdown from './pages/TicketCategoryBreakdown';
import ExecutiveSummary from './pages/ExecutiveSummary';
import Help from './pages/Help';
import Login from './components/Login';
import AssetManagement from './pages/AssetManagement';
import TeamManagement from './pages/TeamManagement';
import AdminCenters from './pages/AdminCenters';
import Suppliers from './pages/Suppliers';
import ClaudeUsageDashboard from './pages/ClaudeUsageDashboard';
import SecureScore from './pages/SecureScore';
import SecurityRecommendations from './pages/SecurityRecommendations';
import ControlCenter from './pages/ControlCenter';
import M365SecureScore from './pages/M365SecureScore';
import CategoryManagement from './pages/CategoryManagement';
import DynamicTitle from './components/DynamicTitle';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';

const HomeRedirect = () => {
  const { isAdmin, isTechnician, loading } = useUser();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (isTechnician() && !isAdmin()) {
    return <Navigate to="/technician" replace />;
  }

  return <Dashboard />;
};

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);
        setAuthError(null);
        
        await authService.initialize();
        const authenticated = authService.isAuthenticated();
        setIsAuthenticated(authenticated);
      } catch (error) {
        console.error('Authentication initialization failed:', error);
        setAuthError('Authentication initialization failed. Please refresh the page.');
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-600 mb-4">{authError}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <UserProvider>
      <DynamicTitle />
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="min-h-screen bg-gray-50">
          <Layout>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/new-ticket" element={<TicketForm />} />
              <Route path="/log-issue" element={<ExergyIssueForm />} />
              <Route path="/ticket/:id" element={<TicketDetail />} />
              <Route path="/technician" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <TechnicianView />
                </ProtectedRoute>
              } />
              <Route path="/sla-report" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <SlaReport />
                </ProtectedRoute>
              } />
              <Route path="/sla-configuration" element={
                <ProtectedRoute requiredRole="Admin">
                  <SlaConfiguration />
                </ProtectedRoute>
              } />
              <Route path="/users" element={
                <ProtectedRoute requiredRole="Admin">
                  <UserManagement />
                </ProtectedRoute>
              } />
              <Route path="/assets" element={
                <ProtectedRoute requiredRole="Admin">
                  <AssetManagement />
                </ProtectedRoute>
              } />
              <Route path="/settings" element={
                <ProtectedRoute requiredRole="Admin">
                  <Settings />
                </ProtectedRoute>
              } />
              <Route path="/system-settings" element={
                <ProtectedRoute requiredRole="Admin">
                  <SystemSettings />
                </ProtectedRoute>
              } />
              <Route path="/email-configuration" element={
                <ProtectedRoute requiredRole="Admin">
                  <EmailConfiguration />
                </ProtectedRoute>
              } />
              <Route path="/reports" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <Reports />
                </ProtectedRoute>
              } />
              <Route path="/ticket-churn-report" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <TicketChurnReport />
                </ProtectedRoute>
              } />
              <Route path="/ticket-category-breakdown" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <TicketCategoryBreakdown />
                </ProtectedRoute>
              } />
              <Route path="/executive-summary" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <ExecutiveSummary />
                </ProtectedRoute>
              } />
              <Route path="/teams" element={
                <ProtectedRoute requiredRole="Admin">
                  <TeamManagement />
                </ProtectedRoute>
              } />
              <Route path="/admin-centers" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <AdminCenters />
                </ProtectedRoute>
              } />
              <Route path="/suppliers" element={
                <ProtectedRoute requiredRole="Admin">
                  <Suppliers />
                </ProtectedRoute>
              } />
              <Route path="/claude-usage" element={
                <ProtectedRoute requiredRole="Admin">
                  <ClaudeUsageDashboard />
                </ProtectedRoute>
              } />
              <Route path="/secure-score" element={
                <ProtectedRoute requiredRole="Admin">
                  <SecureScore />
                </ProtectedRoute>
              } />
              <Route path="/security-recommendations" element={
                <ProtectedRoute requiredRole="Admin">
                  <SecurityRecommendations />
                </ProtectedRoute>
              } />
              <Route path="/control-center" element={
                <ProtectedRoute requiredRole="Admin">
                  <ControlCenter />
                </ProtectedRoute>
              } />
              <Route path="/m365-secure-score" element={
                <ProtectedRoute requiredRole="Admin">
                  <M365SecureScore />
                </ProtectedRoute>
              } />
              <Route path="/categories" element={
                <ProtectedRoute requiredRole="Admin">
                  <CategoryManagement />
                </ProtectedRoute>
              } />
              <Route path="/projects" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <Projects />
                </ProtectedRoute>
              } />
              <Route path="/projects/:id" element={
                <ProtectedRoute requiredRole="AdminOrTechnician">
                  <ProjectDetail />
                </ProtectedRoute>
              } />
              <Route path="/help" element={<Help />} />
            </Routes>
          </Layout>
        </div>
      </Router>
    </UserProvider>
  );
}

export default App; 